import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { ArrowLeft, Calendar, HelpCircle } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

const getStatusColor = (status: string) => {
  const isActive = status ? "Active" : "Inactive";
  switch (isActive) {
    case "Active":
      return "bg-green-100 text-green-800";
    case "Inactive":
      return "bg-gray-100 text-gray-800";
  }
};

export const ScheduleProfileCard: React.FC<{ data: any }> = ({ data }) => {
  const navigate = useNavigate();

  const scheduleFields = [
    {
      label: "Schedule ID",
      value: data?.scheduleNo || "NA",
      tooltip: "Unique system-generated identifier for the schedule.",
    },
    {
      label: "Schedule Name",
      value: data?.scheduleName || "NA",
      tooltip: "Name that describes the schedule’s scope and objective.",
    },
    {
      label: "Schedule Type",
      value: data?.scheduleTypeDisplay || "NA",
      tooltip:
        "Indicates whether this is Preventive Maintenance or Inspection.",
    },
    {
      label: "Created On",
      value: data?.createdDate || "NA",
      tooltip: "Date when this schedule was created.",
    },
    {
      label: "Created By",
      value: data?.createdUserRemoteName || "NA",
      tooltip: "User who created this schedule.",
    },
    {
      label: "Description",
      value: data?.taskDescription || "NA",
      tooltip: "Detailed purpose and coverage of the schedule.",
    },
  ];
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <ArrowLeft
              className="w-5 h-5 cursor-pointer text-gray-700"
              onClick={() => navigate(-1)}
            />
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="flex item-center gap-2">
                <div>
                  <CardTitle className="page-title">
                    {data?.scheduleNo}
                  </CardTitle>
                </div>
                <div className="mt-1">
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Unique system-generated identifier for the schedule.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <p className="page-subtitle mt-1">
                Comprehensive schedule information and trigger details
              </p>
            </div>
          </div>
          <Badge className={getStatusColor(data?.isActive)}>
            {data?.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scheduleFields.map((field, index) => (
            <ValueLabel
              label={field.label}
              value={field.value}
              tooltip={field.tooltip}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
